import type { StatsigLocaleDynamicConfig, PhoneConfig } from '@src/config/localisation/statsig-locale-config.types'

/**
 * US/CA fallback national-number regex used when a locale config carries no
 * `phone.regex`. Matches the legacy US/CA rule (exactly 10 national digits) so
 * callers without a resolved locale config keep their behaviour.
 */
export const DEFAULT_PHONE_REGEX = String.raw`^\d{10}$`

/**
 * Strip the locale's dial code prefix from a stored phone number so the
 * remaining national digits can be pre-filled into a national-only input
 * (e.g. the address edit form) or fed into a display formatter.
 *
 * Returns the phone unchanged when the prefix doesn't match — legacy
 * data saved without any country code is left alone. The backend
 * (customer-services) normalises every write to E.164 via libphonenumber,
 * so the canonical input here is always `+<dial-code><national-digits>`.
 */
export function stripPhoneCountryCode(
  phoneNumber: string | null | undefined,
  phoneCountryCode: string | null | undefined,
): string | null | undefined {
  if (!phoneNumber) return phoneNumber
  if (phoneCountryCode && phoneNumber.startsWith(phoneCountryCode)) {
    return phoneNumber.slice(phoneCountryCode.length)
  }
  return phoneNumber
}

/**
 * Reduce any phone input to its national digits: strip the locale dial code
 * (via {@link stripPhoneCountryCode}) and then drop every non-digit. This is the
 * canonical form validation runs against, so both `+447911123456` and a
 * nationally-typed `07911 123456` collapse to the same digits for a UK locale.
 */
export function getNationalPhoneDigits(
  phoneNumber: string | null | undefined,
  phoneCountryCode: string | null | undefined,
): string {
  return (stripPhoneCountryCode(phoneNumber, phoneCountryCode) ?? '').replace(/\D/g, '')
}

/** Prepend the national trunk prefix (e.g. '0') when not already present. */
export function applyTrunkPrefix(nationalDigits: string, trunkPrefix?: string | null): string {
  if (!nationalDigits || !trunkPrefix) return nationalDigits
  return nationalDigits.startsWith(trunkPrefix) ? nationalDigits : `${trunkPrefix}${nationalDigits}`
}

/**
 * DISPLAY: format a stored phone for the address book.
 *
 * The (NNN) NNN-NNNN mask is a North American convention; it is applied only
 * when the locale's dial code is `+1` and the cleaned national number is exactly
 * 10 digits. Other locales (e.g. UK `+44`) display the national digits with the
 * locale's trunk prefix prepended (UK `07825598628`), since the parenthesised
 * mask would be misleading elsewhere.
 */
export function formatStoredPhoneNumber(
  phoneNumber: string | null | undefined,
  phoneConfig?: PhoneConfig | null,
): string | null | undefined {
  if (!phoneNumber) return phoneNumber
  const countryCode = phoneConfig?.countryCode
  const cleaned = getNationalPhoneDigits(phoneNumber, countryCode)
  if (countryCode === '+1' && cleaned.length === 10) {
    // eslint-disable-next-line i18next/no-literal-string
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`
  }
  return applyTrunkPrefix(cleaned, phoneConfig?.trunkPrefix)
}

/**
 * INPUT PREFILL: the national value (with trunk prefix) to pre-fill the
 * edit-form phone field. UK numbers get the trunk `0` prepended so the user
 * sees and edits `07825598628`; US/CA have no trunk prefix so the national
 * digits are returned as-is.
 */
export function getNationalPhoneForInput(
  phoneNumber: string | null | undefined,
  phoneConfig?: PhoneConfig | null,
): string | null | undefined {
  if (!phoneNumber) return phoneNumber
  return applyTrunkPrefix(getNationalPhoneDigits(phoneNumber, phoneConfig?.countryCode), phoneConfig?.trunkPrefix)
}

/**
 * INPUT CAP: limit a phone input value to the locale's max national digit count
 * (`phone.maxLength`, incl. the trunk prefix — UK 11, US/CA 10). Strips non-digits
 * to count, and when the digit count exceeds the cap, truncates to that many digits.
 * Returns the value unchanged when no cap is configured (legacy behaviour). Prevents
 * over-long entries like `9993345456343423432` in the address phone field.
 */
export function capPhoneInputLength(value: string, phoneConfig?: PhoneConfig | null): string {
  const maxLength = phoneConfig?.maxLength
  if (!value || !maxLength) return value
  const digits = value.replace(/\D/g, '')
  return digits.length > maxLength ? digits.slice(0, maxLength) : value
}

/**
 * Build the national-number RegExp from the locale's `phone.regex`, for consumers
 * (e.g. react-hook-form `pattern`) that validate the raw input value rather than
 * calling {@link validatePhoneForLocale}. Falls back to {@link DEFAULT_PHONE_REGEX}
 * (US/CA 10-digit) when the config carries no regex.
 */
export function getPhoneNumberPattern(localeConfig?: StatsigLocaleDynamicConfig | null): RegExp {
  return new RegExp(localeConfig?.phone?.regex || DEFAULT_PHONE_REGEX)
}

/**
 * Validate a phone number against the locale's `phone.regex`.
 *
 * Mirrors `validateZipCode` (config-driven, no country branching): the dial code
 * (`phone.countryCode`) and non-digits are stripped, then the locale regex is
 * tested against the national value. For UK the regex requires a leading 0, so a
 * pre-filled `07825598628` passes while a bare `7825598628` is rejected. When no
 * config is supplied, falls back to the US/CA 10-digit default.
 */
export function validatePhoneForLocale(
  phoneNumber: string | null | undefined,
  localeConfig?: StatsigLocaleDynamicConfig | null,
): boolean {
  if (!phoneNumber) return false
  const national = getNationalPhoneDigits(phoneNumber, localeConfig?.phone?.countryCode)
  if (!national) return false
  return getPhoneNumberPattern(localeConfig).test(national)
}
